import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, Plus, Trash2, Loader2, FileText, AlertTriangle, CheckCircle2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { ElevationThumb } from "@/components/ElevationThumb";

const STATUS = {
  processing: { t: "AI extracting", c: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  ready: { t: "Ready", c: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  failed: { t: "Failed", c: "text-rose-300 border-rose-500/40 bg-rose-500/10" },
  empty: { t: "No openings found", c: "text-slate-300 border-slate-600 bg-slate-800" },
};

const ReviewChips = ({ r, total }) => (!r ? null : (
  <div className="mt-2 flex items-center gap-2 text-[11px] font-mono" data-testid="project-card-review">
    <span className="flex items-center gap-1 text-emerald-300"><CheckCircle2 size={12} />{r.approved}/{total} approved</span>
    {r.open > 0 && <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300" data-testid="project-card-open"><MessageSquare size={11} />{r.open} open</span>}
  </div>
));

const ProjectCard = ({ p, onOpen, onDelete, review }) => {
  const s = STATUS[p.status] || STATUS.ready;
  const w = p.items.filter((i) => i.type === "window").length, d = p.items.length - w;
  return (
    <div data-testid={`project-card-${p.project_id}`} onClick={onOpen}
      className="group cursor-pointer rounded-lg border border-slate-800 bg-[#111827] hover:border-cyan-500/50 transition-colors duration-200 overflow-hidden">
      <div className="h-40 bg-[#0B0F19] cad-grid p-4 grid place-items-center relative">
        {p.items[0] ? <div className="w-full h-full"><ElevationThumb item={p.items[0]} size={160} /></div>
          : p.status === "processing" ? <Loader2 className="animate-spin text-cyan-400" />
          : p.status === "failed" ? <AlertTriangle className="text-rose-400" /> : <FileText className="text-slate-600" />}
        <button data-testid={`delete-project-${p.project_id}`} onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-md bg-slate-900/90 text-slate-400 opacity-0 group-hover:opacity-100 hover:text-rose-400 transition-opacity">
          <Trash2 size={15} />
        </button>
      </div>
      <div className="p-4 border-t border-slate-800">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading font-semibold truncate" data-testid="project-card-name">{p.name}</h3>
          <span className={`shrink-0 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${s.c}`} data-testid="project-card-status">{s.t}</span>
        </div>
        <p className="mt-2 font-mono text-xs text-slate-500">{w} windows · {d} doors · {new Date(p.updated_at).toLocaleDateString()}{p.created_by ? ` · ${p.created_by.split("@")[0]}` : ""}</p>
        <ReviewChips r={review} total={p.items.length} />
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [projects, setProjects] = useState(null);
  const [review, setReview] = useState({});
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const input = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/projects").then(({ data }) => setProjects(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); api.get("/feedback/summary").then(({ data }) => setReview(data)).catch(() => {}); }, [load]);
  useEffect(() => {
    if (!projects?.some((p) => p.status === "processing")) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [projects, load]);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("Please upload a PDF file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/projects/upload", fd);
      toast.success("Uploaded — AI is reading your drawing");
      navigate(`/project/${data.project_id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };
  const createBlank = async () => {
    const { data } = await api.post("/projects", { name: "Untitled elevation set" });
    navigate(`/project/${data.project_id}`);
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/projects/${p.project_id}`);
    toast.success(`"${p.name}" deleted`, {
      duration: 8000,
      action: { label: "Undo", onClick: async () => { await api.post(`/projects/${p.project_id}/restore`); toast.success("Project restored"); load(); } },
    });
    load();
  };

  return (
    <div className="min-h-screen bg-[#090D16] text-slate-100">
      <AppHeader><span className="font-mono text-xs uppercase tracking-wider text-slate-500">Projects</span></AppHeader>
      <main className="px-6 lg:px-12 py-12 max-w-[1500px]">
        <div className="flex flex-wrap items-end gap-4 mb-10 rise">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Elevation sets</h1>
            <p className="mt-3 text-slate-400">Upload a PDF drawing or schedule to generate system aluminium elevations.</p>
          </div>
          <button data-testid="new-blank-project-btn" onClick={createBlank}
            className="ml-auto h-10 px-4 rounded-md border border-slate-700 text-sm flex items-center gap-2 hover:border-cyan-500 hover:text-cyan-300 transition-colors">
            <Plus size={16} /> Draw from scratch
          </button>
        </div>

        <div data-testid="pdf-dropzone" onClick={() => !uploading && input.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files[0]); }}
          className={`rise cursor-pointer rounded-lg border-2 border-dashed p-10 md:p-14 flex flex-col md:flex-row items-center gap-6 transition-colors duration-200 ${drag ? "border-cyan-400 bg-cyan-500/5" : "border-slate-700 hover:border-cyan-600 bg-[#0B0F19]"}`}>
          <div className="h-16 w-16 rounded-lg bg-cyan-500/10 border border-cyan-500/30 grid place-items-center">
            {uploading ? <Loader2 className="animate-spin text-cyan-400" size={28} /> : <UploadCloud className="text-cyan-400" size={28} />}
          </div>
          <div className="text-center md:text-left">
            <p className="font-heading text-xl font-semibold">{uploading ? "Uploading drawing…" : "Drop a PDF drawing here, or click to browse"}</p>
            <p className="mt-1 text-sm text-slate-400">Single window/door drawing or a compiled set · up to 30 MB</p>
          </div>
          <input ref={input} type="file" accept="application/pdf,.pdf" hidden data-testid="pdf-file-input" onChange={(e) => upload(e.target.files[0])} />
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="projects-grid">
          {projects === null && <Loader2 className="animate-spin text-slate-500" />}
          {projects?.length === 0 && <p className="text-slate-500 text-sm" data-testid="no-projects">No projects yet — upload your first drawing above.</p>}
          {projects?.map((p) => <ProjectCard key={p.project_id} p={p} review={review[p.project_id]} onOpen={() => navigate(`/project/${p.project_id}`)} onDelete={() => remove(p)} />)}
        </div>
      </main>
    </div>
  );
}
